import { motion } from "framer-motion";
import { fadeInUp, staggerContainer, viewportConfig } from "@/lib/motion";
import { useParallax } from "@/lib/useParallax";
import { Car, Sun, Radio, ShieldCheck, Cog, Building2 } from "lucide-react";

const INDUSTRIES = [
  { icon: Car, title: "Electric Vehicles", desc: "Battery packs for 2W, 3W, 4W, and commercial EVs across OEM and fleet segments." },
  { icon: Sun, title: "Renewable Energy", desc: "Storage systems for solar-battery hybrids, wind integration, and off-grid microgrids." },
  { icon: Radio, title: "Telecom Infrastructure", desc: "Lithium-ion backup power for cell towers, data centers, and fiber network nodes." },
  { icon: ShieldCheck, title: "Defense & Aerospace", desc: "Ruggedized battery systems for portable power, UAVs, and field equipment." },
  { icon: Cog, title: "Industrial Automation", desc: "High-cycle battery solutions for AGVs, robotics, and material handling equipment." },
  { icon: Building2, title: "Smart Infrastructure", desc: "Energy storage for smart cities, EV charging stations, and integrated building energy systems." },
];

export default function IndustriesSection() {
  const { ref, y } = useParallax(0.3);
  return (
    <section ref={ref} id="industries" data-testid="industries-section" className="relative py-10 md:py-14 lg:py-20 bg-[#040D1C] noise-overlay overflow-hidden">
      <motion.div style={{ y }} className="section-glow absolute inset-0 pointer-events-none will-change-transform" />
      <motion.div
        style={{ y }}
        aria-hidden
        className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-[#1E96FC]/8 blur-3xl pointer-events-none will-change-transform"
      />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportConfig} variants={fadeInUp} className="mb-4">
          <span className="text-xs uppercase tracking-[0.3em] text-[#1E96FC]">
            // Industries Served
          </span>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={viewportConfig} variants={fadeInUp} className="mb-10 md:mb-16">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-100 leading-tight">
            <span className="block whitespace-nowrap">Powering sectors that</span>
            <span className="block text-[#1E96FC]">define progress</span>
          </h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={staggerContainer}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
        >
          {INDUSTRIES.map((ind, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              data-testid={`industry-card-${i}`}
              className="glass-card rounded-sm p-5 md:p-7 group hover:bg-[#003771]/20 transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#1E96FC]/40 via-[#1E96FC]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="flex items-center gap-3 mb-2">
                <ind.icon className="w-[1em] h-[1em] text-[#1E96FC] flex-shrink-0" />
                <h4 className="text-base font-bold uppercase text-slate-200 tracking-wide leading-none">
                  {ind.title}
                </h4>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed pl-[calc(1em+0.75rem)]">
                {ind.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
